/**
 * The domain's own view of email (docs/EMAIL.md).
 *
 * The domain must NEVER see Microsoft Graph types. The ingestion adapter
 * translates a Graph `message` resource into an `InboundHelpdeskMessage`
 * before anything else touches it; the outbound adapter takes an
 * `OutboundHelpdeskMessage` and drives the create-draft / patch / send /
 * read-back-Message-ID dance described in docs/EMAIL.md §5. Nothing in
 * `apps/api` or `apps/worker`'s domain logic should import a Graph SDK type.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------
export const EMAIL_AUTH_RESULTS = [
    'PASS',
    'FAIL',
    'SOFTFAIL',
    'NEUTRAL',
    'NONE',
    'TEMPERROR',
    'PERMERROR',
];
export const emailAuthResultSchema = z.enum(EMAIL_AUTH_RESULTS);
/**
 * The already-selected `Authentication-Results` verdict — selection by
 * `authserv-id` (docs/EMAIL.md §3) has already happened by the time this
 * shape exists; there is no field here for "the other" instances an
 * attacker may have injected, because the domain must never see them.
 */
export const emailAuthSchema = z.object({
    spf: emailAuthResultSchema,
    dkim: emailAuthResultSchema,
    dmarc: emailAuthResultSchema,
    /** Microsoft's secondary signal for intra-org display-name spoofing. */
    compauth: emailAuthResultSchema.nullable(),
    /** The authserv-id of the instance that was trusted, kept for forensics. */
    authServId: z.string(),
    /**
     * True only when Exchange Online itself authenticated the sender as a
     * mailbox of OUR tenant (`X-MS-Exchange-Organization-AuthAs: Internal` +
     * `X-MS-Exchange-CrossTenant-Id` = our tenant). Mail between two mailboxes
     * of the same tenant never leaves Microsoft, so it carries `dmarc=none` —
     * this is the signal that authenticates it instead (docs/EMAIL.md §3).
     */
    orgInternal: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Inbound
// ---------------------------------------------------------------------------
export const emailAddressSchema = z.object({
    address: z.string().email(),
    displayName: z.string().optional(),
});
export const inboundAttachmentSchema = z.object({
    filename: z.string(),
    declaredMime: z.string(),
    byteSize: z.number().int().nonnegative(),
    /** Maps to `cid:` references in the HTML body when `isInline`. */
    contentId: z.string().optional(),
    isInline: z.boolean(),
});
/**
 * The RAW body (`bodyText` / `bodyHtml`) is ALWAYS retained. Quote-stripping
 * is a downstream presentation concern — it produces a separate, additional
 * view — and must never be the only copy: over-stripping can silently cut
 * genuine new content that follows something resembling a quote header,
 * which would otherwise look like the user said nothing (docs/EMAIL.md §8).
 */
export const inboundHelpdeskMessageSchema = z.object({
    externalMessageId: z.string(),
    internetMessageId: z.string(),
    inReplyTo: z.string().nullable(),
    references: z.array(z.string()),
    from: emailAddressSchema,
    to: z.array(emailAddressSchema),
    cc: z.array(emailAddressSchema),
    subject: z.string().nullable(),
    bodyText: z.string().nullable(),
    bodyHtml: z.string().nullable(),
    receivedAt: z.date(),
    attachments: z.array(inboundAttachmentSchema),
    headers: z.record(z.string()),
    auth: emailAuthSchema,
});
// ---------------------------------------------------------------------------
// Outbound
// ---------------------------------------------------------------------------
/**
 * `Message-ID` is system-managed and read-only in Graph — `sendMail`
 * returns no body at all, and `internetMessageHeaders` accepts only custom
 * `x-`-prefixed headers. The real wire `Message-ID` is read back with a
 * `$select` AFTER send and persisted as its own `OUTBOUND` `email_messages`
 * row (docs/EMAIL.md §5). It is never set here, and `headers` on this shape
 * only ever holds custom `x-` headers for the same reason.
 */
export const outboundHelpdeskMessageSchema = z.object({
    ticketId: z.string().uuid(),
    toAddresses: z.array(emailAddressSchema).min(1),
    ccAddresses: z.array(emailAddressSchema),
    subject: z.string(),
    bodyText: z.string().nullable(),
    bodyHtml: z.string().nullable(),
    inReplyToInternetMessageId: z.string().nullable(),
    headers: z.record(z.string().regex(/^x-/i, 'only custom x- headers may be set on outbound mail'), z.string()),
});
// ---------------------------------------------------------------------------
// Thread correlation
// ---------------------------------------------------------------------------
/**
 * Matches the Prisma `CorrelationMethod` enum exactly.
 *
 * `X-Helpdesk-Ticket` was REMOVED from inbound correlation in the Phase 1
 * review: a reply is a new MIME message and mail clients do not echo
 * arbitrary `X-` headers from the message being replied to, so it never
 * fired for genuine human mail (docs/EMAIL.md §4). It MUST NOT be
 * reintroduced here — it remains available only as a distinct mechanism for
 * deliberate machine-to-machine integrations that choose to echo it back,
 * which is not this list.
 */
export const CORRELATION_METHODS = [
    'IN_REPLY_TO',
    'REFERENCES',
    'VERP_ADDRESS',
    'SUBJECT_TAG_PARTICIPANT',
    'CONVERSATION_ID_CORROBORATED',
    'NONE_NEW_TICKET',
];
export const correlationMethodSchema = z.enum(CORRELATION_METHODS);
/**
 * `ambiguous` must never silently attach to an arbitrary candidate — it
 * belongs in front of a human. `untrusted-thread-match` (a thread match
 * whose sender failed DMARC) goes to a technician REVIEW queue, never to
 * silent quarantine: silent quarantine creates pressure to merge tickets by
 * hand, which reintroduces the spoofing risk the gate exists to prevent
 * (docs/EMAIL.md §3, decision D-04).
 */
export const correlationResultSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('matched'),
        ticketId: z.string().uuid(),
        method: correlationMethodSchema,
        confidence: z.number().min(0).max(1),
    }),
    z.object({
        kind: z.literal('new'),
    }),
    z.object({
        kind: z.literal('ambiguous'),
        candidateTicketIds: z.array(z.string().uuid()).min(2),
    }),
    z.object({
        kind: z.literal('untrusted-thread-match'),
        ticketId: z.string().uuid(),
    }),
]);
// ---------------------------------------------------------------------------
// Loop and storm protection (docs/EMAIL.md §7)
// ---------------------------------------------------------------------------
/**
 * Any one of these causes an inbound message to be RECORDED (inserted into
 * `email_messages` for idempotency — see docs/EMAIL.md §2) and then DROPPED
 * without creating or updating a ticket. Recorded-then-dropped, never
 * silently discarded outright and never turned into a new ticket: a bounce
 * or DSN in particular is routed to an alert, because a bounce of our own
 * resolution email means a requester never received their confirmation
 * link, which is an operational problem, not spam.
 */
export const LOOP_GUARD_HEADERS = [
    {
        id: 'AUTO_SUBMITTED',
        header: 'Auto-Submitted',
        description: 'Present and not "no" (RFC 3834).',
    },
    {
        id: 'AUTO_RESPONSE_SUPPRESS',
        header: 'X-Auto-Response-Suppress',
        description: 'Present at all.',
    },
    {
        id: 'PRECEDENCE_BULK',
        header: 'Precedence',
        description: 'Value is bulk, list, or junk.',
    },
    {
        id: 'MAILING_LIST',
        header: 'List-Id / List-Unsubscribe',
        description: 'Either header present (RFC 2919 / 2369).',
    },
    {
        id: 'BOUNCE_REPORT',
        header: 'Content-Type',
        description: 'multipart/report; report-type=delivery-status — a bounce (RFC 3464).',
    },
    {
        id: 'READ_RECEIPT',
        header: 'Content-Type',
        description: 'message/disposition-notification — a read receipt (RFC 8098).',
    },
    {
        id: 'SELF_SENDER',
        header: null,
        description: 'Sender is the helpdesk address, one of its aliases, or a known service account.',
    },
    {
        id: 'SENDER_RATE_LIMIT',
        header: null,
        description: 'More than 10 inbound messages from one address within 5 minutes.',
    },
];
//# sourceMappingURL=email-contracts.js.map